import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["writeTab", "previewTab", "writeContent", "previewContent", "bodyElements", "preview"];
  static values = {
    logoUrl: String,
    orgName: String,
    socialMedia: Object,
    website: String,
    location: String,
    country: String,
    templateBody: Array,
    buttonBgColor: String,
    buttonTextColor: String
  };

  connect() {
    // Initialize immediately
    this.initialize();
    
    // Also listen for Turbo Frame load events in case the modal is loaded dynamically
    this.boundInitialize = () => this.initialize();
    document.addEventListener("turbo:frame-load", this.boundInitialize);
  }

  disconnect() {
    // Clean up event listener
    if (this.boundInitialize) {
      document.removeEventListener("turbo:frame-load", this.boundInitialize);
    }
  }

  initialize() {
    // Clear any existing elements first (in case of form re-render with errors)
    if (this.hasBodyElementsTarget) {
      this.bodyElementsTarget.innerHTML = "";
    }
    
    // Initialize element index
    this.elementIndex = 0;
    
    // Load template body elements if any
    if (this.templateBodyValue && Array.isArray(this.templateBodyValue) && this.templateBodyValue.length > 0) {
      this.templateBodyValue.forEach((element, index) => {
        if (element && element.type) {
          this.addElementFromTemplate(element, index);
        }
      });
      this.elementIndex = this.templateBodyValue.length;
    } else {
      this.addElement("paragraph");
    }
    this.updatePreview();
  }

  showWrite(event) {
    event.preventDefault();
    this.activateTab(this.writeTabTarget, this.previewTabTarget);
    this.writeContentTarget.classList.remove("hidden");
    this.previewContentTarget.classList.add("hidden");
  }

  showPreview(event) {
    event.preventDefault();
    this.updatePreview();
    this.activateTab(this.previewTabTarget, this.writeTabTarget);
    this.writeContentTarget.classList.add("hidden");
    this.previewContentTarget.classList.remove("hidden");
  }

  activateTab(activeTab, inactiveTab) {
    activeTab.classList.add("ui-tab-item-active");
    inactiveTab.classList.remove("ui-tab-item-active");
  }

  addParagraph(event) {
    this.addElement("paragraph", event);
  }

  addButton(event) {
    this.addElement("button", event);
  }

  addLink(event) {
    this.addElement("link", event);
  }

  addImage(event) {
    this.addElement("image", event);
  }

  addElement(type, event) {
    if (event) event.preventDefault();
    const template = document.getElementById(`${type}-email-element-template`);
    if (!template) {
      console.error(`Template not found: ${type}-email-element-template`);
      return;
    }

    const index = this.elementIndex++;
    const html = template.innerHTML.replace(/\{\{INDEX\}\}/g, index);
    
    const div = document.createElement("div");
    div.innerHTML = html;
    const element = div.firstElementChild;
    
    if (!element) {
      console.error(`Failed to create element from template: ${type}`);
      return;
    }
    
    this.bodyElementsTarget.appendChild(element);
    this.updatePreview();
  }

  addElementFromTemplate(element, index) {
    const type = element.type || element["type"];
    const template = document.getElementById(`${type}-email-element-template`);
    if (!template) return;

    let html = template.innerHTML.replace(/\{\{INDEX\}\}/g, index);
    
    // Fill in values from template - escape HTML for safety
    const escapeHtml = (str) => {
      const div = document.createElement("div");
      div.textContent = str || "";
      return div.innerHTML;
    };
    
    if (type === "paragraph") {
      const content = element.content || element["content"] || "";
      html = html.replace(/\{\{CONTENT\}\}/g, escapeHtml(content));
    } else if (type === "button" || type === "link") {
      const text = element.text || element["text"] || "";
      const url = element.url || element["url"] || "";
      html = html.replace(/\{\{TEXT\}\}/g, escapeHtml(text));
      html = html.replace(/\{\{URL\}\}/g, escapeHtml(url));
    } else if (type === "image") {
      const url = element.url || element["url"] || "";
      const alt = element.alt || element["alt"] || "";
      html = html.replace(/\{\{URL\}\}/g, escapeHtml(url));
      html = html.replace(/\{\{ALT\}\}/g, escapeHtml(alt));
    }
    
    const div = document.createElement("div");
    div.innerHTML = html;
    const elementDiv = div.firstElementChild;
    
    this.bodyElementsTarget.appendChild(elementDiv);
  }

  removeElement(event) {
    const index = event.currentTarget.dataset.index;
    const element = this.bodyElementsTarget.querySelector(`[data-element-index="${index}"]`);
    if (element) {
      element.remove();
      this.updatePreview();
    }
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return;

    // Get subject from form
    const subject = document.querySelector("input[name=\"subject\"]")?.value || "";

    // Get body elements
    const bodyElements = [];
    this.bodyElementsTarget.querySelectorAll("[data-element-type]").forEach(elementDiv => {
      const type = elementDiv.dataset.elementType;
      const data = { type: type };
      elementDiv.querySelectorAll("input, textarea, select").forEach(input => {
        if (input.type === "hidden") return;
        const nameMatch = input.name.match(/\[([^\]]+)\]$/);
        if (nameMatch) {
          const name = nameMatch[1];
          data[name] = input.value;
        }
      });
      bodyElements.push(data);
    });

    // Render preview
    this.renderPreview(subject, bodyElements);
  }

  renderPreview(subject, bodyElements) {
    let html = "<div class=\"email-preview-shell\">";

    // Header with logo
    html += "<div class=\"email-preview-panel email-preview-header email-preview-header--left\">";
    if (this.logoUrlValue && this.logoUrlValue.trim() !== "") {
      html += `<img src="${this.escapeHtml(this.logoUrlValue)}" alt="${this.escapeHtml(this.orgNameValue || "Logo")}" class="email-preview-logo" />`;
    } else {
      html += "<div class=\"email-preview-logo-placeholder\">[Your Logo]</div>";
    }
    html += "</div>";

    // Body
    html += "<div class=\"email-preview-panel\">";

    // Render body elements
    bodyElements.forEach(element => {
      switch(element.type) {
      case "paragraph": {
        const content = element.content || "";
        let formattedContent = content
          .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
          .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
          .replace(/\n/g, "<br>");
        formattedContent = this.escapeHtmlButPreserveTags(formattedContent);
        html += `<div class="email-preview-content email-preview-content--spaced">${formattedContent}</div>`;
        break;
      }
      case "button":
        if (element.url) {
          html += `<div class="email-preview-block email-preview-header--left"><a href="${this.escapeHtml(element.url)}" class="email-preview-button">${this.escapeHtml(element.text || "Click Here")}</a></div>`;
        }
        break;
      case "link":
        if (element.url) {
          html += `<div class="email-preview-block"><a href="${this.escapeHtml(element.url)}" class="email-preview-link">${this.escapeHtml(element.text || "Link Text")}</a></div>`;
        }
        break;
      case "image":
        if (element.url) {
          html += `<div class="email-preview-block email-preview-header--left"><img src="${this.escapeHtml(element.url)}" alt="${this.escapeHtml(element.alt || "")}" class="email-preview-image" /></div>`;
        }
        break;
      }
    });

    html += "</div>";

    // Footer
    html += "<div class=\"email-preview-footer\">";

    if (this.orgNameValue) {
      html += `<div class="email-preview-company">${this.escapeHtml(this.orgNameValue)}</div>`;
    }

    const contactInfo = [];
    if (this.locationValue) contactInfo.push(this.escapeHtml(this.locationValue));
    if (this.countryValue) contactInfo.push(this.escapeHtml(this.countryValue));
    if (this.websiteValue) {
      const websiteUrl = this.websiteValue.startsWith("http://") || this.websiteValue.startsWith("https://") ? this.websiteValue : `https://${this.websiteValue}`;
      contactInfo.push(`<a href="${this.escapeHtml(websiteUrl)}" class="email-preview-link email-preview-link--plain">${this.escapeHtml(this.websiteValue)}</a>`);
    }

    if (contactInfo.length > 0) {
      html += `<div class="email-preview-contact">${contactInfo.join(" | ")}</div>`;
    }

    // Social media
    const socialMedia = Object.entries(this.socialMediaValue || {}).filter(([_, url]) => url && url.trim() !== "");
    if (socialMedia.length > 0) {
      html += "<div class=\"email-preview-social\">";
      socialMedia.forEach(([platformName, url]) => {
        const displayName = platformName.charAt(0).toUpperCase() + platformName.slice(1);
        html += `<a href="${this.escapeHtml(url)}" class="email-preview-link email-preview-link--plain email-preview-social-link">${this.escapeHtml(displayName)}</a>`;
      });
      html += "</div>";
    }

    html += "</div>";
    html += "</div>";

    this.previewTarget.innerHTML = html;
  }

  escapeHtml(unsafe) {
    const div = document.createElement("div");
    div.textContent = unsafe;
    return div.innerHTML;
  }

  escapeHtmlButPreserveTags(text) {
    const placeholders = {};
    let placeholderIndex = 0;
    let safe = text.replace(/<(strong|em|br)\/?>|<\/(strong|em)>/gi, (match) => {
      const key = `__PLACEHOLDER_${placeholderIndex}__`;
      placeholders[key] = match;
      placeholderIndex++;
      return key;
    });
    
    safe = this.escapeHtml(safe);
    
    Object.keys(placeholders).forEach(key => {
      safe = safe.replace(key, placeholders[key]);
    });
    
    return safe;
  }
}
